import React, { useState, useEffect, useCallback } from "react";
import { proformaApi, bankDetailsApi } from "../lib/api";
import { toast } from "sonner";
import {
  FileText, Search, Filter, Plus, Edit2, Trash2, Download, ArrowRight,
  X, Check, Loader2, ChevronDown, Eye, CreditCard, Package
} from "lucide-react";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "JPY", "AED", "NZD", "CAD", "CHF", "SGD", "AUD"];
const STATUSES = ["draft", "sent", "paid", "cancelled", "converted"];
const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700",
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  converted: "bg-purple-100 text-purple-700",
};
const CURRENCY_SYMBOLS = {
  INR: "\u20B9", USD: "$", EUR: "\u20AC", GBP: "\u00A3", JPY: "\u00A5",
  AED: "AED", NZD: "NZ$", CAD: "C$", CHF: "CHF", SGD: "S$", AUD: "A$",
};

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ProformaInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [editInvoice, setEditInvoice] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const res = await proformaApi.getAll(params);
      setInvoices(res.data.invoices || []);
      setPagination(p => ({ ...p, pages: res.data.pages || 1, total: res.data.total || 0 }));
    } catch (err) { toast.error("Failed to load invoices"); }
    finally { setLoading(false); }
  }, [pagination.page, statusFilter]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => {
    bankDetailsApi.getAll().then(r => setBankAccounts(r.data || [])).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await proformaApi.delete(id); toast.success("Deleted"); fetchInvoices(); }
    catch { toast.error("Failed to delete"); }
  };

  const handleConvert = async (id) => {
    if (!window.confirm("Convert this invoice to an admin order?")) return;
    try {
      const res = await proformaApi.convertToOrder(id);
      toast.success(`Converted to order: ${res.data.order_number}`);
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to convert"); }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      // Send BOTH the httpOnly cookie (credentials: include) and the
      // localStorage Bearer token — either will satisfy backend auth even
      // when cross-site cookie policies block the cookie in production.
      const headers = {};
      try {
        const tk = localStorage.getItem("crm_token");
        if (tk) headers["Authorization"] = `Bearer ${tk}`;
      } catch (err) { console.debug("proforma: localStorage unavailable, falling back to cookie auth", err); }
      const resp = await fetch(`${API_URL}/proforma-invoices/${invoice.id}/pdf`, {
        credentials: "include",
        headers,
      });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.proforma_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Failed to download PDF"); }
  };

  const cs = (currency) => CURRENCY_SYMBOLS[currency] || currency;

  const filtered = invoices.filter(inv => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return inv.customer_name?.toLowerCase().includes(q) || inv.proforma_number?.toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[--bg-cream] p-4 md:p-6" data-testid="proforma-invoices-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[--text-primary]" style={{ fontFamily: "Playfair Display, serif" }}>
            Proforma Invoices
          </h1>
          <p className="text-sm text-[--text-muted]">{pagination.total} invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:w-56">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[--text-muted]" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..." className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-[--border] rounded-lg focus:ring-2 focus:ring-[--accent] outline-none" data-testid="search-invoices-input" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-[--border] rounded-lg px-3 py-2 bg-white" data-testid="status-filter-select">
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm py-2 px-4 flex items-center gap-1.5" data-testid="create-invoice-btn">
            <Plus size={16} /> New Invoice
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[--border] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[--accent]" size={32} /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[--text-muted]">
            <FileText size={48} className="mx-auto mb-3 opacity-30" />
            <p>No invoices found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[--bg-cream] border-b border-[--border]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-[--text-muted]">Invoice #</th>
                  <th className="text-left px-4 py-3 font-medium text-[--text-muted]">Customer</th>
                  <th className="text-left px-4 py-3 font-medium text-[--text-muted]">Currency</th>
                  <th className="text-left px-4 py-3 font-medium text-[--text-muted]">Total</th>
                  <th className="text-left px-4 py-3 font-medium text-[--text-muted]">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-[--text-muted]">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-[--text-muted]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr key={inv.id} className="border-b border-[--border] hover:bg-[--bg-cream]/50" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-4 py-3 font-medium">{inv.proforma_number}</td>
                    <td className="px-4 py-3">
                      <div>{inv.customer_name}</div>
                      {inv.customer_phone && <div className="text-[11px] text-[--text-muted]">{inv.customer_phone}</div>}
                    </td>
                    <td className="px-4 py-3">{inv.currency}</td>
                    <td className="px-4 py-3 font-medium">{cs(inv.currency)}{(inv.total_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[inv.status] || "bg-gray-100"}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[--text-muted]">{inv.created_at ? new Date(inv.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setViewInvoice(inv)} className="btn-icon" title="View" data-testid={`view-invoice-${inv.id}`}><Eye size={14} /></button>
                        <button onClick={() => setEditInvoice(inv)} className="btn-icon" title="Edit" data-testid={`edit-invoice-${inv.id}`}><Edit2 size={14} /></button>
                        <button onClick={() => handleDownloadPdf(inv)} className="btn-icon" title="Download PDF" data-testid={`download-pdf-${inv.id}`}><Download size={14} /></button>
                        {inv.status === "paid" && (
                          <button onClick={() => handleConvert(inv.id)} className="btn-icon text-purple-600 hover:bg-purple-50" title="Convert to Order" data-testid={`convert-invoice-${inv.id}`}><ArrowRight size={14} /></button>
                        )}
                        <button onClick={() => handleDelete(inv.id)} className="btn-icon text-red-500 hover:bg-red-50" title="Delete" data-testid={`delete-invoice-${inv.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[--border]">
            <p className="text-sm text-[--text-muted]">Page {pagination.page} of {pagination.pages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))} disabled={pagination.page === 1} className="btn-secondary text-sm">Previous</button>
              <button onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))} disabled={pagination.page === pagination.pages} className="btn-secondary text-sm">Next</button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <InvoiceFormModal bankAccounts={bankAccounts} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); fetchInvoices(); }} />}
      {editInvoice && <InvoiceFormModal invoice={editInvoice} bankAccounts={bankAccounts} onClose={() => setEditInvoice(null)} onSaved={() => { setEditInvoice(null); fetchInvoices(); }} />}
      {viewInvoice && <InvoiceViewModal invoice={viewInvoice} onClose={() => setViewInvoice(null)} onDownload={() => handleDownloadPdf(viewInvoice)} />}
    </div>
  );
}

/* ---- Invoice Form Modal (Create / Edit) ---- */
function InvoiceFormModal({ invoice, bankAccounts = [], onClose, onSaved }) {
  const isEdit = !!invoice;
  const [form, setForm] = useState({
    customer_name: invoice?.customer_name || "",
    customer_email: invoice?.customer_email || "",
    customer_phone: invoice?.customer_phone || "",
    billing_address: invoice?.billing_address || "",
    shipping_address: invoice?.shipping_address || "",
    currency: invoice?.currency || "INR",
    shipping_amount: invoice?.shipping_amount || 0,
    overall_discount_percent: invoice?.overall_discount_percent || 0,
    overall_discount_amount: invoice?.overall_discount_amount || 0,
    gst_percent: invoice?.gst_percent || 0,
    bank_details_id: invoice?.bank_details_id || "",
    notes: invoice?.notes || "",
    status: invoice?.status || "draft",
  });
  const [items, setItems] = useState(
    invoice?.items?.length ? invoice.items.map(it => ({ ...it })) : [{ title: "", description: "", quantity: 1, unit_price: 0, discount_percent: 0, discount_amount: 0 }]
  );
  const [saving, setSaving] = useState(false);

  const addItem = () => setItems(prev => [...prev, { _uiId: `item-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, title: "", description: "", quantity: 1, unit_price: 0, discount_percent: 0, discount_amount: 0 }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const calcItemTotal = (item) => {
    let t = item.quantity * item.unit_price;
    if (item.discount_percent > 0) t *= (1 - item.discount_percent / 100);
    else if (item.discount_amount > 0) t -= item.discount_amount;
    return Math.max(0, t);
  };

  const subtotal = items.reduce((s, it) => s + calcItemTotal(it), 0);
  let discounted = subtotal;
  if (form.overall_discount_percent > 0) discounted = subtotal * (1 - form.overall_discount_percent / 100);
  else if (form.overall_discount_amount > 0) discounted = subtotal - form.overall_discount_amount;
  const beforeTax = discounted + (parseFloat(form.shipping_amount) || 0);
  const gstAmt = form.gst_percent > 0 ? beforeTax * (form.gst_percent / 100) : 0;
  const total = beforeTax + gstAmt;
  const sym = CURRENCY_SYMBOLS[form.currency] || form.currency;

  const handleSubmit = async () => {
    if (!form.customer_name || !form.billing_address || items.length === 0) {
      toast.error("Fill customer name, billing address, and at least one item"); return;
    }
    if (items.some(it => !it.title || it.unit_price <= 0)) {
      toast.error("Each item needs a title and price > 0"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, items, shipping_amount: parseFloat(form.shipping_amount) || 0, overall_discount_percent: parseFloat(form.overall_discount_percent) || 0, overall_discount_amount: parseFloat(form.overall_discount_amount) || 0, gst_percent: parseFloat(form.gst_percent) || 0 };
      if (isEdit) {
        await proformaApi.update(invoice.id, payload);
        toast.success("Invoice updated");
      } else {
        const res = await proformaApi.create(payload);
        toast.success(`Invoice created: ${res.data.proforma_number}`);
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="invoice-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[--border] sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold" style={{ fontFamily: "Playfair Display, serif" }}>
            {isEdit ? `Edit: ${invoice.proforma_number}` : "New Proforma Invoice"}
          </h2>
          <button onClick={onClose} className="btn-icon"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Customer */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer Name *" value={form.customer_name} onChange={v => setForm(f => ({ ...f, customer_name: v }))} testId="pi-customer-name" />
            <Field label="Phone" value={form.customer_phone} onChange={v => setForm(f => ({ ...f, customer_phone: v }))} testId="pi-customer-phone" />
            <Field label="Email" value={form.customer_email} onChange={v => setForm(f => ({ ...f, customer_email: v }))} testId="pi-customer-email" />
            <div>
              <label className="text-[10px] font-medium text-[--text-muted] uppercase mb-1 block">Currency</label>
              <select value={form.currency} onChange={(e) => setForm(f => ({ ...f, currency: e.target.value }))} className="w-full text-sm border border-[--border] rounded-lg px-3 py-2" data-testid="pi-currency">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <Field label="Billing Address *" value={form.billing_address} onChange={v => setForm(f => ({ ...f, billing_address: v }))} testId="pi-billing-address" textarea />
          <Field label="Shipping Address" value={form.shipping_address} onChange={v => setForm(f => ({ ...f, shipping_address: v }))} testId="pi-shipping-address" textarea />

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-medium text-[--text-muted] uppercase">Line Items</label>
              <button onClick={addItem} className="text-[11px] text-[--accent] hover:underline flex items-center gap-1" data-testid="add-item-btn"><Plus size={12} /> Add Item</button>
            </div>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={item._uiId || `row-${idx}`} className="bg-[--bg-cream] rounded-lg p-3 space-y-2" data-testid={`item-row-${idx}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 grid grid-cols-4 gap-2">
                      <div className="col-span-2">
                        <input type="text" value={item.title} onChange={(e) => updateItem(idx, "title", e.target.value)} placeholder="Item title *" className="w-full text-sm border border-[--border] rounded-lg px-2 py-1.5" data-testid={`item-title-${idx}`} />
                      </div>
                      <input type="number" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", parseInt(e.target.value) || 1)} placeholder="Qty" min="1" className="text-sm border border-[--border] rounded-lg px-2 py-1.5" data-testid={`item-qty-${idx}`} />
                      <input type="number" value={item.unit_price} onChange={(e) => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} placeholder="Price" className="text-sm border border-[--border] rounded-lg px-2 py-1.5" data-testid={`item-price-${idx}`} />
                    </div>
                    <button onClick={() => removeItem(idx)} className="btn-icon text-red-400 hover:text-red-600 mt-0.5" disabled={items.length === 1}><X size={14} /></button>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <input type="text" value={item.description || ""} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Description (optional)" className="flex-1 text-xs border border-[--border] rounded px-2 py-1" />
                    <span className="text-[--text-muted] whitespace-nowrap">Disc%:</span>
                    <input type="number" value={item.discount_percent || ""} onChange={(e) => updateItem(idx, "discount_percent", parseFloat(e.target.value) || 0)} className="w-16 text-xs border border-[--border] rounded px-2 py-1" />
                    <span className="font-medium text-[--text-secondary] whitespace-nowrap">{sym}{calcItemTotal(item).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals & extras */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Shipping" value={form.shipping_amount} onChange={v => setForm(f => ({ ...f, shipping_amount: v }))} type="number" testId="pi-shipping" />
            <Field label="Overall Disc %" value={form.overall_discount_percent} onChange={v => setForm(f => ({ ...f, overall_discount_percent: v, overall_discount_amount: 0 }))} type="number" testId="pi-overall-disc-pct" />
            <Field label="Overall Disc Amt" value={form.overall_discount_amount} onChange={v => setForm(f => ({ ...f, overall_discount_amount: v, overall_discount_percent: 0 }))} type="number" testId="pi-overall-disc-amt" />
            <Field label="GST %" value={form.gst_percent} onChange={v => setForm(f => ({ ...f, gst_percent: v }))} type="number" testId="pi-gst" />
          </div>

          {/* Bank details */}
          {bankAccounts.length > 0 && (
            <div>
              <label className="text-[10px] font-medium text-[--text-muted] uppercase mb-1 block">Bank Account</label>
              <select value={form.bank_details_id} onChange={(e) => setForm(f => ({ ...f, bank_details_id: e.target.value }))} className="w-full text-sm border border-[--border] rounded-lg px-3 py-2" data-testid="pi-bank-select">
                <option value="">No bank details</option>
                {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.account_name} - {b.bank_name}</option>)}
              </select>
            </div>
          )}

          <Field label="Notes" value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} testId="pi-notes" textarea />

          {isEdit && (
            <div>
              <label className="text-[10px] font-medium text-[--text-muted] uppercase mb-1 block">Status</label>
              <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className="w-full text-sm border border-[--border] rounded-lg px-3 py-2" data-testid="pi-status">
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          )}

          {/* Summary */}
          <div className="bg-[--bg-cream] rounded-lg p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-[--text-muted]">Subtotal</span><span>{sym}{subtotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
            {(form.overall_discount_percent > 0 || form.overall_discount_amount > 0) && (
              <div className="flex justify-between text-red-600"><span>Discount</span><span>-{sym}{(subtotal - discounted).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
            )}
            {parseFloat(form.shipping_amount) > 0 && (
              <div className="flex justify-between"><span className="text-[--text-muted]">Shipping</span><span>{sym}{parseFloat(form.shipping_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
            )}
            {gstAmt > 0 && (
              <div className="flex justify-between"><span className="text-[--text-muted]">GST ({form.gst_percent}%)</span><span>{sym}{gstAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
            )}
            <div className="flex justify-between font-bold text-base pt-1 border-t border-[--border]">
              <span>Total</span><span>{sym}{total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-[--border] sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="btn-primary" data-testid="save-invoice-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} /> {isEdit ? "Save Changes" : "Create Invoice"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- Invoice View Modal ---- */
function InvoiceViewModal({ invoice, onClose, onDownload }) {
  const sym = CURRENCY_SYMBOLS[invoice.currency] || invoice.currency;
  const fmt = (n) => (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="invoice-view-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[--border] sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold" style={{ fontFamily: "Playfair Display, serif" }}>{invoice.proforma_number}</h2>
          <div className="flex items-center gap-2">
            <button onClick={onDownload} className="btn-secondary text-sm flex items-center gap-1" data-testid="view-download-pdf"><Download size={14} /> PDF</button>
            <button onClick={onClose} className="btn-icon"><X size={18} /></button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex justify-between">
            <div>
              <p className="font-semibold text-base">{invoice.customer_name}</p>
              {invoice.customer_phone && <p className="text-sm text-[--text-muted]">{invoice.customer_phone}</p>}
              {invoice.customer_email && <p className="text-sm text-[--text-muted]">{invoice.customer_email}</p>}
            </div>
            <div className="text-right">
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[invoice.status] || "bg-gray-100"}`}>{invoice.status}</span>
              <p className="text-xs text-[--text-muted] mt-1">{invoice.created_at?.slice(0, 10)}</p>
            </div>
          </div>
          {invoice.billing_address && <div><p className="text-[10px] text-[--text-muted] uppercase font-medium">Billing Address</p><p className="text-sm">{invoice.billing_address}</p></div>}
          {invoice.shipping_address && invoice.shipping_address !== invoice.billing_address && <div><p className="text-[10px] text-[--text-muted] uppercase font-medium">Shipping Address</p><p className="text-sm">{invoice.shipping_address}</p></div>}

          {/* Items table */}
          <table className="w-full text-sm border border-[--border] rounded-lg overflow-hidden">
            <thead className="bg-[--bg-cream]">
              <tr>
                <th className="text-left px-3 py-2 text-[--text-muted] font-medium">#</th>
                <th className="text-left px-3 py-2 text-[--text-muted] font-medium">Item</th>
                <th className="text-center px-3 py-2 text-[--text-muted] font-medium">Qty</th>
                <th className="text-right px-3 py-2 text-[--text-muted] font-medium">Price</th>
                <th className="text-right px-3 py-2 text-[--text-muted] font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.items || []).map((it, i) => (
                <tr key={`${it.title || "row"}-${i}`} className="border-t border-[--border]">
                  <td className="px-3 py-2">{i + 1}</td>
                  <td className="px-3 py-2"><div className="font-medium">{it.title}</div>{it.description && <div className="text-[11px] text-[--text-muted]">{it.description}</div>}</td>
                  <td className="px-3 py-2 text-center">{it.quantity}</td>
                  <td className="px-3 py-2 text-right">{sym}{fmt(it.unit_price)}</td>
                  <td className="px-3 py-2 text-right font-medium">{sym}{fmt(it.item_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="bg-[--bg-cream] rounded-lg p-3 space-y-1 text-sm ml-auto max-w-xs">
            <div className="flex justify-between"><span className="text-[--text-muted]">Subtotal</span><span>{sym}{fmt(invoice.subtotal)}</span></div>
            {(invoice.overall_discount_percent > 0 || invoice.overall_discount_amount > 0) && (
              <div className="flex justify-between text-red-600"><span>Discount</span><span>-{sym}{fmt((invoice.subtotal || 0) - (invoice.discounted_subtotal || 0))}</span></div>
            )}
            {(invoice.shipping_amount || 0) > 0 && <div className="flex justify-between"><span className="text-[--text-muted]">Shipping</span><span>{sym}{fmt(invoice.shipping_amount)}</span></div>}
            {(invoice.gst_amount || 0) > 0 && <div className="flex justify-between"><span className="text-[--text-muted]">GST ({invoice.gst_percent}%)</span><span>{sym}{fmt(invoice.gst_amount)}</span></div>}
            <div className="flex justify-between font-bold text-base pt-1 border-t border-[--border]"><span>Total</span><span>{sym}{fmt(invoice.total_amount)}</span></div>
          </div>

          {invoice.bank_details && (
            <div className="bg-blue-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800 mb-1">Bank Details</p>
              <p className="text-blue-700">{invoice.bank_details.account_name} - {invoice.bank_details.bank_name}</p>
              <p className="text-blue-600 text-xs">A/C: {invoice.bank_details.account_number}{invoice.bank_details.ifsc_code ? ` | IFSC: ${invoice.bank_details.ifsc_code}` : ""}</p>
            </div>
          )}

          {invoice.notes && <div><p className="text-[10px] text-[--text-muted] uppercase font-medium">Notes</p><p className="text-sm">{invoice.notes}</p></div>}
        </div>
      </div>
    </div>
  );
}

/* ---- Reusable Field ---- */
function Field({ label, value, onChange, testId, type = "text", textarea = false }) {
  return (
    <div>
      <label className="text-[10px] font-medium text-[--text-muted] uppercase mb-1 block">{label}</label>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full text-sm border border-[--border] rounded-lg px-3 py-2" rows={2} data-testid={testId} />
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full text-sm border border-[--border] rounded-lg px-3 py-2" data-testid={testId} />
      )}
    </div>
  );
}
